import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  collection,
  addDoc,
  setDoc,
  doc,
  getDocs,
  deleteDoc,
  query,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";

// 🔄 UPSERT CATEGORY
type CategoryPayload = {
  id?: string;
  title: string;
  slug: string;
  description?: string;
  parent?: string | null;
};

export const upsertCategory = createAsyncThunk(
  "blog/upsertCategory",
  async ({ id, title, slug, description, parent }: CategoryPayload) => {
    const ref = doc(db, "categories", id || slug);
    const payload = {
      title,
      slug,
      description: description || "",
      parent: parent || null,
      type: "category",
      updatedAt: Timestamp.now(),
      createdAt: Timestamp.now(),
    };
    await setDoc(ref, payload, { merge: true });
    return { id: ref.id, ...payload };
  }
);

// 🔄 UPSERT PAGE
export const upsertPage = createAsyncThunk(
  "blog/upsertPage",
  async ({
    id,
    title,
    slug,
    parent,
    content = "",
  }: {
    id?: string;
    title: string;
    slug: string;
    parent?: string;
    content?: string;
  }) => {
    const ref = doc(db, "pages", id || slug);
    const payload = {
      title,
      slug,
      parent: parent || null,
      content,
      type: "page",
      published: false,
      updatedAt: Timestamp.now(),
      createdAt: Timestamp.now(),
    };
    await setDoc(ref, payload, { merge: true });
    return { id: ref.id, ...payload };
  }
);

// 🔄 UPSERT POST
export const upsertPost = createAsyncThunk(
  "blog/upsertPost",
  async ({
    id,
    title,
    slug,
    content,
    category,
    parentPage,
    published = false,
    featuredImage = "",
    description = "",
  }: {
    id?: string;
    title: string;
    slug: string;
    content: string;
    category?: string;
    parentPage?: string;
    published?: boolean;
    featuredImage?: string;
    description?: string;
  }) => {
    const ref = doc(db, "posts", id || slug);
    const payload = {
      title,
      slug,
      content,
      category: category || null,
      parentPage: parentPage || null,
      published,
      type: "post",
      featuredImage,
      description,
      updatedAt: Timestamp.now(),
      createdAt: Timestamp.now(),
    };
    await setDoc(ref, payload, { merge: true });
    return { id: ref.id, ...payload };
  }
);

// 🗑 DELETE PAGE
export const deletePage = createAsyncThunk("blog/deletePage", async (id: string) => {
  const ref = doc(db, "pages", id);
  await deleteDoc(ref);
  return id;
});

// 🗑 DELETE POST
export const deletePost = createAsyncThunk("blog/deletePost", async (id: string) => {
  const ref = doc(db, "posts", id);
  await deleteDoc(ref);
  return id;
});

// 📥 FETCH
export const fetchCategories = createAsyncThunk("blog/fetchCategories", async () => {
  const q = query(collection(db, "categories"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
});

export const fetchPages = createAsyncThunk("blog/fetchPages", async () => {
  const q = query(collection(db, "pages"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
});

export const fetchPosts = createAsyncThunk("blog/fetchPosts", async () => {
  const q = query(collection(db, "posts"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
});

// SLICE
const blogSlice = createSlice({
  name: "blog",
  initialState: {
    categories: [] as any[],
    pages: [] as any[],
    posts: [] as any[],
    status: "idle",
    error: null as string | null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      .addCase(fetchPages.fulfilled, (state, action) => {
        state.pages = action.payload;
      })
      .addCase(fetchPosts.fulfilled, (state, action) => {
        state.posts = action.payload;
      })
      .addCase(upsertCategory.fulfilled, (state, action) => {
        const index = state.categories.findIndex((c) => c.id === action.payload.id);
        if (index >= 0) state.categories[index] = action.payload;
        else state.categories.push(action.payload);
      })
      .addCase(upsertPage.fulfilled, (state, action) => {
        const index = state.pages.findIndex((p) => p.id === action.payload.id);
        if (index >= 0) state.pages[index] = action.payload;
        else state.pages.push(action.payload);
      })
      .addCase(upsertPost.fulfilled, (state, action) => {
        const index = state.posts.findIndex((p) => p.id === action.payload.id);
        if (index >= 0) state.posts[index] = action.payload;
        else state.posts.push(action.payload);
      })
      .addCase(deletePage.fulfilled, (state, action) => {
        state.pages = state.pages.filter((p) => p.id !== action.payload);
      })
      .addCase(deletePost.fulfilled, (state, action) => {
        state.posts = state.posts.filter((p) => p.id !== action.payload);
      });
  },
});

export default blogSlice.reducer;
